import { useMemo } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import { useTranslations } from 'hooks/useTranslations';
import Slider, {
  SliderAriaFormat,
  SliderFormat,
  SliderInputControls,
  SliderValidators
} from '@ingka/slider';
import { cmToMm, inchToMm, mmToCm, mmToInch } from 'utils/measurementUtils';
import { parseImperial, parseMetric } from 'utils/units/parse';
import { toDisplayCm, toDisplayIn } from 'utils/units/format';
import { isMobile } from 'utils/platform';

const StyledAdjustableSlider = styled(Slider)`
  margin-inline: 2.25rem;
  margin-block-start: ${isMobile() ? '' : '1rem'};

  & .slider__controls {
    display: ${isMobile() ? 'none' : ''};
  }

  & #adjustable-width-slider__slider-form-field {
    display: ${isMobile() ? 'none' : ''};
    margin-inline: 0;
  }
  & #adjustable-width-slider__slider-form-field::before {
    content: none;
  }
`;

type AdjustableSliderProps = {
  initialSize: number;
  unit: string;
  onSliderChange: (sliderValue: number) => any;
};

export const RANGE_CM = { min: 20, max: 30 };
export const RANGE_IN = { min: 7.87, max: 11.81 }; // decimal values of inches w. fractions: 7 7/8" & 11 3/4".
export const MM_TO_INCH = 0.3937;

const AdjustableSlider = ({ initialSize, unit, onSliderChange }: AdjustableSliderProps) => {
  const { formatMessage } = useIntl();
  const { useMetric } = useTranslations();

  const inputMode = useMetric ? 'decimal' : 'text';
  const RANGE = useMetric ? RANGE_CM : RANGE_IN;
  const STEP = useMetric ? 1 : MM_TO_INCH;

  const value = useMemo(() => getStartValue(mmToCm(initialSize), useMetric, RANGE), [
    initialSize,
    RANGE,
    useMetric
  ]);

  const handleOnChange = (values: number[]) => {
    if (isWithinRange(values[0], RANGE)) {
      const valueInMm = getValueInMm(values[0], useMetric);
      onSliderChange(valueInMm);
    }
  };

  const handleValueEntered = (values: (number | string)[]) => {
    const value = Number(values[0]);
    if (value < 7) return onSliderChange(200);
    if (isWithinRange(value, RANGE)) {
      const valueInMm = getValueInMm(value, useMetric);
      onSliderChange(valueInMm);
    }
  };

  const isWithinRangeValidation: SliderValidators = {
    validate: ({ value }) => isWithinRange(Number(value), RANGE),
    message: formatMessage({ id: 'storageone.outOfRangeValidationMessage' })
  };

  const format: SliderFormat = {
    to: num => {
      return useMetric ? toDisplayCm(num) : toDisplayIn(num);
    },
    from: str => {
      return useMetric ? parseMetric(str) : parseImperial(str) ?? 0;
    }
  };

  const ariaFormat: SliderAriaFormat = {
    to: num => {
      return useMetric ? toDisplayCm(num) : toDisplayIn(num);
    },
    from: format.from
  };

  return (
    <StyledAdjustableSlider
      id="adjustable-width-slider"
      label={
        isMobile() ? '' : formatMessage({ id: 'storageone.adjustableSectionWidthSliderTitle' })
      }
      suffixLabel={useMetric ? unit : undefined}
      format={format}
      range={RANGE}
      start={value}
      step={STEP}
      ariaFormat={ariaFormat}
      ariaLabels={formatMessage({ id: 'storageone.sliderStartValueAriaLabel' })}
      onChange={handleOnChange}
      inputControls={
        isMobile() ? (
          undefined
        ) : (
          <SliderInputControls
            onValueEntered={handleValueEntered}
            inputSize="medium"
            inputMode={inputMode}
            formattedEdit
          />
        )
      }
      validators={[isWithinRangeValidation]}
    />
  );
};

export const isWithinRange = (value: number, range: { min: number; max: number }) =>
  value >= range.min && value <= range.max;

const getStartValue = (size: number, useMetric: boolean, range: { min: number; max: number }) => {
  // For imperial, sometimes the precision leads to incorrect value in mm,
  // rounding to 2 decimal points solves the issue.
  const val = useMetric ? size : Math.round(mmToInch(size * 10) * 100) / 100;
  return isWithinRange(val, range) ? val : range.min;
};

const getValueInMm = (value: number, useMetric: boolean) =>
  useMetric ? cmToMm(value) : inchToMm(value, true);

export default AdjustableSlider;
